import axiosInstance from '../variables/api/axiosInstance';
import { avecLibelleTraduit } from '../i18n/libelles';

const BASE = '/v1/attestations';

const unwrapList = (res) => {
  const d = res?.data?.data;
  if (Array.isArray(d)) return d;
  if (d?.content && Array.isArray(d.content)) return d.content;
  return [];
};

const unwrapPaginated = (res) => {
  const d = res?.data?.data;
  const list = Array.isArray(d) ? d : (d?.content || []);
  return {
    items: list,
    totalElements: res?.data?.totalElements ?? d?.totalElements ?? list.length,
    currentPage: res?.data?.currentPage ?? d?.number ?? 0,
    pageSize: res?.data?.pageSize ?? d?.size ?? list.length,
    totalPages: res?.data?.totalPages ?? d?.totalPages ?? 1,
  };
};

const unwrap = (res) => res?.data?.data;

// ============== COMMANDES ==============
export async function listerCommandes(page = 0, size = 20) {
  return unwrapPaginated(await axiosInstance.get(`${BASE}/commandes?page=${page}&size=${size}`));
}
export async function getCommande(commandeTrackingId) {
  return unwrap(await axiosInstance.get(`${BASE}/commandes/${commandeTrackingId}`));
}
export async function listerCommandesParOrganisme(orgId, page = 0, size = 20) {
  return unwrapPaginated(await axiosInstance.get(`${BASE}/commandes/organisme/${orgId}?page=${page}&size=${size}`));
}
export async function passerCommande(payload) {
  // payload: { organismeTrackingId, quantite, nomBeneficiaireCheque }
  return unwrap(await axiosInstance.post(`${BASE}/commandes`, payload));
}
export async function genererProforma(commandeId) {
  return unwrap(await axiosInstance.post(`${BASE}/commandes/${commandeId}/proforma`));
}
export async function genererFactureDefinitive(commandeId) {
  return unwrap(await axiosInstance.post(`${BASE}/commandes/${commandeId}/facture`));
}
export async function marquerLivre(commandeId, dateLivraison) {
  return unwrap(await axiosInstance.patch(`${BASE}/commandes/${commandeId}/livrer`, null, { params: { dateLivraison } }));
}
export async function annulerCommande(commandeId) {
  return unwrap(await axiosInstance.patch(`${BASE}/commandes/${commandeId}/annuler`));
}
export async function solderCommande(commandeId) {
  return unwrap(await axiosInstance.patch(`${BASE}/commandes/${commandeId}/solder`));
}

// ============== LOTS ==============
export async function listerLots(page = 0, size = 20) {
  return unwrapPaginated(await axiosInstance.get(`${BASE}/lots?page=${page}&size=${size}`));
}
export async function listerLotsDisponibles() {
  return unwrapList(await axiosInstance.get(`${BASE}/lots/disponibles`));
}
export async function getLot(lotId) {
  return unwrap(await axiosInstance.get(`${BASE}/lots/${lotId}`));
}
export async function creerLot(payload) {
  // { referenceLot, nomFournisseur, numeroBonCommande, quantite, numeroDebutSerie, numeroFinSerie, prixUnitaireAchat, dateCommande, dateLivraisonFournisseur }
  return unwrap(await axiosInstance.post(`${BASE}/lots`, payload));
}
export async function modifierLot(lotId, payload) {
  return unwrap(await axiosInstance.put(`${BASE}/lots/${lotId}`, payload));
}

// ============== CHÈQUES ==============
export async function listerCheques(page = 0, size = 20) {
  return unwrapPaginated(await axiosInstance.get(`${BASE}/cheques?page=${page}&size=${size}`));
}
export async function getCheque(chequeId) {
  return unwrap(await axiosInstance.get(`${BASE}/cheques/${chequeId}`));
}
export async function listerChequesParCommande(commandeId) {
  return unwrapList(await axiosInstance.get(`${BASE}/commandes/${commandeId}/cheques`));
}
export async function enregistrerCheque(payload) {
  // { factureTrackingId, numeroCheque, montant, banqueEmettrice, dateEmission, dateReception }
  return unwrap(await axiosInstance.post(`${BASE}/cheques`, payload));
}
export async function encaisserCheque(chequeId, dateEncaissement) {
  return unwrap(await axiosInstance.patch(`${BASE}/cheques/${chequeId}/encaisser`, null,
    dateEncaissement ? { params: { dateEncaissement } } : undefined));
}
export async function annulerCheque(chequeId, motif) {
  return unwrap(await axiosInstance.patch(`${BASE}/cheques/${chequeId}/annuler`, null, { params: { motif } }));
}

// ============== FACTURES ==============
export async function listerFactures(page = 0, size = 20) {
  return unwrapPaginated(await axiosInstance.get(`${BASE}/factures?page=${page}&size=${size}`));
}
export async function getFacture(factureId) {
  return unwrap(await axiosInstance.get(`${BASE}/factures/${factureId}`));
}
export async function listerFacturesParCommande(commandeId) {
  return unwrapList(await axiosInstance.get(`${BASE}/commandes/${commandeId}/factures`));
}

// ============== TRANCHES DE LIVRAISON ==============
export async function livrerTranche(commandeId, payload) {
  // { lotTrackingId, numeroDebutSerie, numeroFinSerie, quantiteLivree, dateLivraison }
  return unwrap(await axiosInstance.post(`${BASE}/commandes/${commandeId}/tranches`, payload));
}
export async function listerTranchesParCommande(commandeId) {
  return unwrapList(await axiosInstance.get(`${BASE}/commandes/${commandeId}/tranches`));
}

// PDF d'une facture (proforma ou définitive). Renvoie un Blob.
export async function telechargerFacturePdf(factureId) {
  const res = await axiosInstance.get(`${BASE}/factures/${factureId}/pdf`, { responseType: 'blob' });
  return res.data;
}

// Ouvre le PDF d'une facture dans un nouvel onglet (preview navigateur)
export async function ouvrirFacturePdf(factureId) {
  const blob = await telechargerFacturePdf(factureId);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Paramètres tarifaires (lus depuis la table parametre côté backend).
// On NE met PAS de fallback en dur : si le backend ne renvoie pas une valeur,
// l'appelant doit afficher un état "tarif non configuré" et bloquer la commande.
export async function getParametresTarifaires() {
  const res = await axiosInstance.get(`${BASE}/parametres`);
  const d = res?.data?.data || {};
  const prix = Number(d.prixUnitaireAttestation);
  const fonds = Number(d.contributionFonds);
  return {
    prixUnitaireAttestation: Number.isFinite(prix) && prix > 0 ? prix : null,
    contributionFonds: Number.isFinite(fonds) && fonds >= 0 ? fonds : null,
  };
}

// ============== UTILITAIRES ==============
// Compagnies membres togolaises uniquement : seules elles peuvent commander
// des attestations Carte Brune auprès du Bureau National Togolais.
export async function listerOrganismesCompagnies() {
  const res = await axiosInstance.get('/v1/organismes?size=200');
  const d = res?.data?.data;
  const list = Array.isArray(d) ? d : (d?.content || []);
  return list.filter(o =>
    o.typeOrganisme === 'COMPAGNIE_MEMBRE'
    && (o.codePays === 'TGO' || o.codePaysBCB === 'TG')
  );
}

// Référentiel des banques partenaires (table `banque`, gérée dans Paramètres > Banques).
export async function listerBanques() {
  return unwrapList(await axiosInstance.get('/v1/banques'));
}

export const STATUTS_COMMANDE = avecLibelleTraduit({
  PROFORMA_EMISE: { color: 'blue',     order: 1 },
  CHEQUE_RECU:    { color: 'cyan',     order: 2 },
  LIVRE:          { color: 'geekblue', order: 3 },
  FACTURE_EMISE:  { color: 'gold',     order: 4 },
  SOLDE:          { color: 'success',  order: 5 },
  ANNULEE:        { color: 'red',      order: 99 },
}, 'referentiels:statutsCommande');

export const STATUTS_CHEQUE_ATT = avecLibelleTraduit({
  RECU:                 { color: 'blue' },
  ENCAISSE:             { color: 'green' },
  REVENU_SANS_PROVISION:{ color: 'red' },
  ANNULE:               { color: 'default' },
}, 'referentiels:statutsChequeAttestation');

export const STATUTS_LOT = avecLibelleTraduit({
  LIVRE:     { color: 'green' },
  DISTRIBUE: { color: 'default' },
}, 'referentiels:statutsLot');

export const TYPES_FACTURE = avecLibelleTraduit({
  PROFORMA:   { color: 'blue' },
  DEFINITIVE: { color: 'green' },
}, 'referentiels:typesFacture');
